

// eslint-disable-next-line
import { handleResponseJson, handleResponseVoid, handleError } from "../../apiUtils";
import { PhotoInfo } from "../Interfaces/PhotoInfo";
import { ScheduleRequest } from "../Interfaces/ScheduleRequest";
import { PositionInfo } from "../Interfaces/PositionInfo";
import { PhotoResult } from "../Interfaces/PhotoResult";
import { QueuePlacementResult } from "../Interfaces/QueuePlacementResult";
import { ScheduleResult } from "../Interfaces/ScheduleResult";
import { ClientSession } from "../Interfaces/ClientSession";
import { Registration } from "../Interfaces/Registration";
import { HeartBeatResult } from "../Interfaces/HeartBeatResult";
import { LiveSwitchTokenResult } from "../Interfaces/LiveSwitchTokenResult";
import { AccessInfo } from "../Interfaces/AccessInfo";

/**
* Accepts the privacy statement.
**/
export function acceptPrivacyStatement(authToken: string): Promise<void> {
    var url = `api/client/session/AcceptPrivacyStatement`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Adds the contact photo.
**/
export function addPolicyHolderPhoto(file: Blob, authToken: string): Promise<PhotoResult> {
    var url = `api/client/session/AddPolicyHolderPhoto`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: file
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Opts out of taking a Portrait photo
**/
export function optOutOfPortraitPhoto(authToken: string): Promise<void> {
    var url = `api/client/session/OptOutOfPortraitPhoto`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Adds a photo.
**/
export function addSessionPhoto(photoId: string, file: Blob, authToken: string): Promise<PhotoResult> {
    var url = `api/client/session/AddSessionPhoto/${encodeURIComponent(photoId)}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: file
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Adds a session photo position.
**/
export function addSessionPhotoPosition(photoId: string, info: PhotoInfo, authToken: string): Promise<void> {
    var url = `api/client/session/AddSessionPhotoPosition/${encodeURIComponent(photoId)}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken, "Content-Type": "application/json" },
                body: JSON.stringify(info)
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Checks the queue placement.
**/
export function checkQueuePlacement(authToken: string): Promise<QueuePlacementResult> {
    var url = `api/client/session/CheckQueuePlacement`;
    return (
        fetch(url,
            {
                method: "get",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Ends the client registration.
**/
export function endClientRegistration(deviceId: string, sessionId: string, authToken: string): Promise<void> {
    var url = `api/client/session/EndClientRegistration/${encodeURIComponent(deviceId)}/${encodeURIComponent(sessionId)}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Ends the client session.
**/
export function endClientSession(authToken: string): Promise<void> {
    var url = `api/client/session/EndClientSession`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Ends the client session for continuation later.
**/
export function endClientSessionAndContinueLater(deviceId: string, sessionId: string, authToken: string): Promise<void> {
    var url = `api/client/session/EndClientSessionAndContinueLater/${encodeURIComponent(deviceId)}/${encodeURIComponent(sessionId)}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Removes the client from session
**/
export function removeClientFromSession(deviceId: string, authToken: string): Promise<void> {
    var url = `api/client/session/RemoveClientFromSession/${encodeURIComponent(deviceId)}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Enters the mentee into the queue.
**/
export function enterQueue(deviceId: string, authToken: string): Promise<QueuePlacementResult> {
    var url = `api/client/session/EnterQueue/${encodeURIComponent(deviceId)}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Gets the available schedule for a GuideStream session based on the assigned mentor and customer specific settings.
**/
export function getAvailableSchedule(request: ScheduleRequest, authToken: string): Promise<ScheduleResult> {
    var url = `api/client/session/GetAvailableSchedule`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken, "Content-Type": "application/json" },
                body: JSON.stringify(request)
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Gets the session information.
**/
export function getSessionInfo(authToken: string): Promise<ClientSession> {
    var url = `api/client/session/GetSessionInfo`;
    return (
        fetch(url,
            {
                method: "get",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Returns session observers currently logged in.
**/
export function getActiveObservers(authToken: string): Promise<Registration[]> {
    var url = `api/client/session/GetActiveObservers`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Returns if session has an active mentee
**/
export function hasActiveMentee(deviceId: string, authToken: string): Promise<boolean> {
    var url = `api/client/session/HasActiveMentee?deviceId=${encodeURIComponent(deviceId)}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* A ping that lets the system know that the client is still active If location api is  enabled it will also have position information
**/
export function heartBeatPing(positions: PositionInfo[], authToken: string): Promise<HeartBeatResult> {
    var url = `api/client/session/HeartBeatPing`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken, "Content-Type": "application/json" },
                body: JSON.stringify(positions)
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Gets a LiveSwitch token for the mentee.
**/
export function liveSwitchToken(deviceId: string, authToken: string): Promise<LiveSwitchTokenResult> {
    var url = `api/client/session/LiveSwitchToken/${encodeURIComponent(deviceId)}`;
    return (
        fetch(url,
            {
                method: "get",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Sets the schedule date time.
**/
export function setScheduleDateTime(scheduleDate: Date, authToken: string): Promise<Date> {
    var url = `api/client/session/SetScheduleDateTime`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken, "Content-Type": "application/json" },
                body: JSON.stringify(scheduleDate)
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Submits the audio rating.
**/
export function submitAudioRating(audioRating: number | null, authToken: string): Promise<void> {
    var url = `api/client/session/SubmitAudioRating/${audioRating}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Submits the feedback.
**/
export function submitFeedback(feedback: string, authToken: string): Promise<void> {
    var url = `api/client/session/SubmitFeedback`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken, "Content-Type": "application/json" },
                body: JSON.stringify(feedback)
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Submits the overall rating.
**/
export function submitOverallRating(overallRating: number | null, authToken: string): Promise<void> {
    var url = `api/client/session/SubmitOverallRating/${overallRating}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Submits the video rating.
**/
export function submitVideoRating(videoRating: number | null, authToken: string): Promise<void> {
    var url = `api/client/session/SubmitVideoRating/${videoRating}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseVoid)
            .catch(handleError)
    );
}

/**
* Gets a token for a quick code.
**/
export function token(quickCode: string): Promise<AccessInfo> {
    var url = `api/client/session/Token?quickCode=${encodeURIComponent(quickCode)}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: {},
                body: null
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}

/**
* Updates the culture (using the admin API).
**/
export function updateCulture(cultureCode: string, authToken: string): Promise<ClientSession> {
    var url = `api/client/session/UpdateCulture/${encodeURIComponent(cultureCode)}`;
    return (
        fetch(url,
            {
                method: "post",
                headers: { "Authorization": "Bearer " + authToken },
                body: null
            })
            .then(handleResponseJson)
            .catch(handleError)
    );
}



